import type { LabOrder, LabOrderMaterial, LabOrderMaterialGroup } from '@/types';

/**
 * ArtDent 261007 — one lab order, several materials.
 *
 * Shared by the form (which paints teeth in these colours) and every screen
 * that names an order's materials, so a material reads the same everywhere.
 */

/**
 * A fill and stroke per material for the tooth chart and its legend. Plain hex
 * because the chart is SVG and the legend uses them as inline styles — no
 * Tailwind class is generated per material.
 */
export const LAB_MATERIAL_COLORS: Record<LabOrderMaterial, { fill: string; stroke: string }> = {
  zirconia: { fill: '#2563eb', stroke: '#1d4ed8' },
  metal_ceramic: { fill: '#d97706', stroke: '#b45309' },
  emax: { fill: '#7c3aed', stroke: '#6d28d9' },
  implant: { fill: '#059669', stroke: '#047857' },
  laser_sinter: { fill: '#db2777', stroke: '#be185d' },
  inlay_onlay: { fill: '#0891b2', stroke: '#0e7490' },
  acetal: { fill: '#57534e', stroke: '#44403c' },
};

/**
 * The order's teeth by material, main material first. Read from the API's
 * `material_groups`; an order fetched without it (or saved before it existed)
 * is one group of its own material.
 */
export function labOrderMaterialGroups(
  order: Pick<LabOrder, 'material' | 'teeth' | 'material_groups'>,
): LabOrderMaterialGroup[] {
  if (order.material_groups && order.material_groups.length > 0) return order.material_groups;

  return [
    {
      material: order.material,
      teeth: (order.teeth ?? []).map((tooth) => tooth.tooth_number).sort((a, b) => a - b),
    },
  ];
}

/** Does this order carry more than one material? */
export const isMultiMaterial = (order: Pick<LabOrder, 'material' | 'teeth' | 'material_groups'>): boolean =>
  labOrderMaterialGroups(order).length > 1;

/**
 * One line for lists and cards: "Zirconia ×3 · Metal-ceramic ×2". A single
 * material reads exactly as it always has — just its name — so an order on
 * one material looks the same as before on every screen.
 */
export function formatMaterialGroups(
  groups: LabOrderMaterialGroup[],
  labelOf: (material: LabOrderMaterial) => string,
): string {
  if (groups.length <= 1) return groups[0] ? labelOf(groups[0].material) : '';

  return groups
    .map((group) => (group.teeth.length > 0 ? `${labelOf(group.material)} ×${group.teeth.length}` : labelOf(group.material)))
    .join(' · ');
}

/**
 * A tooth can carry several materials (ArtDent 261007 — 11 as an implant AND
 * a zirconia crown). Each tooth's materials, read off the groups, in group
 * order (main material first).
 */
export function materialsByTooth(groups: LabOrderMaterialGroup[]): Record<number, LabOrderMaterial[]> {
  const byTooth: Record<number, LabOrderMaterial[]> = {};
  groups.forEach((group) =>
    group.teeth.forEach((tooth) => {
      byTooth[tooth] = [...(byTooth[tooth] ?? []), group.material];
    }),
  );
  return byTooth;
}

/**
 * The chart paint per tooth: its material's colour, or — for a tooth with
 * several — stripes of each (`fills`), outlined in the first one's stroke.
 */
export function labToothPaints(
  byTooth: Record<number | string, LabOrderMaterial[]>,
): Record<number, { fill: string; stroke: string; fills?: string[] }> {
  return Object.fromEntries(
    Object.entries(byTooth)
      .filter(([, materials]) => materials.length > 0)
      .map(([tooth, materials]) => [
        Number(tooth),
        materials.length > 1
          ? { ...LAB_MATERIAL_COLORS[materials[0]], fills: materials.map((m) => LAB_MATERIAL_COLORS[m].fill) }
          : LAB_MATERIAL_COLORS[materials[0]],
      ]),
  );
}
